import { useEffect, useState, useCallback, useRef } from "react";
import {
  StackSimple, Plus, LockKey, Globe, User, CaretDown, X, ShieldCheck, PencilSimple, TrashSimple,
  DownloadSimple, UploadSimple, Question, Bell, HandWaving,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "../components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Toaster } from "../components/ui/sonner";
import { toast } from "sonner";
import { api } from "../lib/api";
import { downloadBlob } from "../lib/api";
import { CATEGORIES, SUBJECTS, CategoryPill, SubjectPill, categoryColor, subjectColor, JobDots } from "../lib/tags";
import { MyJobsDialog } from "../features/MyJobsDialog";
import { DataSources } from "../features/DataSources";
import { RuleStack, newLevel } from "../features/RuleStack";
import { ResultsGrid } from "../features/ResultsGrid";
import { VerificationPlayground } from "../features/VerificationPlayground";
import { ModelManager } from "../features/ModelManager";
import { GuideDialog } from "../features/GuideDialog";

function computeStats(rows) {
  const stats = { gold: 0, silver: 0, bronze: 0, red_flag: 0, manual: 0, unmatched: 0, ignored: 0, total: 0 };
  for (const r of rows) {
    const rt = r._rating;
    if (rt === "subtotal") continue;
    stats.total++;
    if (rt === "ignored") stats.ignored++;
    else if (rt === "manual") stats.manual++;
    else if (r._matched === false || rt === "unmatched") stats.unmatched++;
    else if (rt in stats) stats[rt]++;
  }
  return stats;
}

function useSession() {
  const [userId, setUserId] = useState(null);
  useEffect(() => {
    let id = localStorage.getItem("rowtool_uid");
    if (id) { setUserId(id); return; }
    api.session().then((r) => { localStorage.setItem("rowtool_uid", r.user_id); setUserId(r.user_id); });
  }, []);
  return userId;
}

export default function RowTool() {
  const userId = useSession();
  const [jobs, setJobs] = useState({ private: [], common: null });
  const [job, setJob] = useState(null);
  const [data, setData] = useState({ results: [], columns: [], stats: {} });
  const [pwdDialog, setPwdDialog] = useState(null); // {mode: 'access'|'publish'}
  const [pwd, setPwd] = useState("");
  const [createForm, setCreateForm] = useState(null); // {name, category, subject}
  const [editForm, setEditForm] = useState(null); // {name, category, subject}
  const [jobFilter, setJobFilter] = useState({ category: "", subject: "" });
  const [myJobsOpen, setMyJobsOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [jobsMenuOpen, setJobsMenuOpen] = useState(false);
  const [activeRowIds, setActiveRowIds] = useState([]);
  const [levels, setLevels] = useState([]);
  const [recallBanner, setRecallBanner] = useState(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const [releaseDialog, setReleaseDialog] = useState(null);

  const refreshJobs = useCallback(async () => {
    if (!userId) return;
    const j = await api.listJobs(userId);
    setJobs(j);
    return j;
  }, [userId]);

  useEffect(() => { refreshJobs(); }, [refreshJobs]);

  useEffect(() => {
    if (!userId) return;
    const t = setInterval(() => refreshJobs(), 25000);
    return () => clearInterval(t);
  }, [userId, refreshJobs]);

  const openJob = useCallback((full) => {
    setJob(full);
    setLevels(full.levels?.length
      ? full.levels.map((l) => ({ ...l, id: l.id || Math.random().toString(36).slice(2) }))
      : [newLevel()]);
    const results = full.results || [];
    setData({
      results,
      columns: full.result_columns || [],
      verifications: full.verifications || [],
      ai_tools: full.ai_tools || [],
      stats: computeStats(results),
    });
  }, []);

  const createJob = () => setCreateForm({ name: `Lavoro ${jobs.private.length + 1}`, category: "", subject: "" });
  const doCreate = async () => {
    const f = createForm;
    if (!f?.name.trim()) return;
    const j = await api.createJob(userId, f.name.trim(), f.category || "", f.subject || "");
    await refreshJobs();
    const full = await api.getJob(j.id);
    openJob(full);
    setCreateForm(null);
    toast.success("Nuovo lavoro creato");
  };

  const selectJob = async (id) => {
    const full = await api.getJob(id);
    openJob(full);
  };

  const submitPwd = async () => {
    if (!pwd.trim()) return;
    try {
      if (pwdDialog.mode === "publish") {
        await api.publish(job.id, pwd);
        toast.success("Lavoro pubblicato in area comune");
        const full = await api.getJob(job.id);
        openJob(full);
      } else {
        const full = await api.commonAccess(pwd, userId);
        openJob(full);
        toast.success("Accesso all'area comune");
      }
      await refreshJobs();
      setPwdDialog(null); setPwd("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const accessCommon = async () => {
    const info = await api.commonInfo();
    if (!info.exists) { toast.message("Nessun lavoro comune attivo. Pubblicane uno privato."); return; }
    if (info.owner_id === userId && (info.release_requests || []).length) { setReleaseDialog(info); return; }
    setPwdDialog({ mode: "access", name: info.name, isOwner: info.owner_id === userId,
      requested: (info.release_requests || []).some((r) => r.user_id === userId) });
  };

  const forceEject = async () => {
    if (!window.confirm("Forzare la chiusura dell'area comune? Il lavoro comune verrà chiuso e svuotato per tutti.")) return;
    try {
      await api.forceEject(userId);
      toast.success("Area comune liberata forzatamente");
      if (job?.visibility === "common") { setJob(null); setData({ results: [], columns: [], stats: {} }); }
      setPwdDialog(null); setPwd("");
      await refreshJobs();
    } catch (e) { toast.error(e.response?.data?.detail || "Impossibile forzare l'eject"); }
  };

  const askRelease = async () => {
    try {
      await api.requestRelease(userId);
      toast.success("Richiesta inviata al creatore dell'area comune");
      setPwdDialog(null); setPwd("");
      await refreshJobs();
    } catch (e) { toast.error(e.response?.data?.detail || "Impossibile inviare la richiesta"); }
  };

  const freeCommon = async () => {
    try {
      await api.closeCommon(releaseDialog.id);
      toast.success("Area comune liberata");
      if (job?.visibility === "common") { setJob(null); setData({ results: [], columns: [], stats: {} }); }
      setReleaseDialog(null);
      await refreshJobs();
    } catch (e) { toast.error("Errore durante la liberazione"); }
  };

  const dismissRelease = async () => {
    try { await api.clearRelease(userId); } catch (e) {}
    setReleaseDialog(null);
    await refreshJobs();
    toast.message("Avvisi azzerati");
  };

  const closeCommon = async () => {
    await api.closeCommon(job.id);
    toast.success("Lavoro comune chiuso e svuotato");
    setJob(null);
    setData({ results: [], columns: [], stats: {} });
    await refreshJobs();
  };

  const isCommon = job?.visibility === "common";
  const commonReleaseCount = (jobs.common && jobs.common.owner_id === userId) ? (jobs.common.release_requests || []).length : 0;
  const recentJobs = [...jobs.private].sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || "")).slice(0, 3);

  const doEdit = async () => {
    const f = editForm;
    if (!f?.name.trim()) return;
    const updated = await api.updateJob(job.id, { name: f.name.trim(), category: f.category || "", subject: f.subject || "" });
    setJob({ ...job, name: updated.name, category: updated.category, subject: updated.subject });
    await refreshJobs();
    setEditForm(null);
    toast.success("Lavoro aggiornato");
  };

  const doUnpublish = async () => {
    const updated = await api.unpublish(job.id, userId);
    setJob(updated);
    await refreshJobs();
    toast.success("Lavoro riportato in privato");
  };

  const doDelete = async () => {
    setDeleteOpen(false);
    if (isCommon) { await closeCommon(); return; }
    await api.deleteJob(job.id);
    setJob(null);
    setData({ results: [], columns: [], stats: {} });
    await refreshJobs();
    toast.success("Lavoro eliminato");
  };

  const fileInputRef = useRef(null);
  const doExportJob = async () => {
    try {
      const blob = await api.exportJob(job.id);
      downloadBlob(blob, `${job.name}.rowtool`);
      toast.success("Lavoro esportato");
    } catch (e) { toast.error("Errore esportazione lavoro"); }
  };
  const doImportJob = async (file) => {
    if (!file) return;
    try {
      const parsed = JSON.parse(await file.text());
      const created = await api.importJob(userId, parsed);
      await refreshJobs();
      const full = await api.getJob(created.id);
      openJob(full);
      toast.success("Lavoro importato");
    } catch (e) { toast.error("File non valido o errore importazione"); }
  };

  return (
    <div className="min-h-screen">
      <Toaster position="bottom-right" theme="light" />
      <input ref={fileInputRef} type="file" accept=".rowtool,.json,application/json" className="hidden"
        data-testid="import-job-input"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; doImportJob(f); }} />

      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-white/75 backdrop-blur-xl border-b border-border">
        <div className="max-w-[1400px] mx-auto px-6 py-3 flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <img src="/rowtool-logo.png" alt="RowTool" className="w-9 h-9 rounded-lg" />
            <div>
              <div className="font-head font-extrabold text-lg leading-none">RowTool</div>
              <div className="text-[10px] text-muted-foreground tech">incrocio dati</div>
            </div>
          </div>

          {job && (
            <div className="flex items-center gap-2 ml-2">
              <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border ${isCommon ? "bg-[hsl(var(--gold)/0.15)] text-[hsl(var(--gold))] border-[hsl(var(--gold)/0.5)]" : "bg-primary/15 text-primary border-primary/40"}`}>
                {isCommon ? <Globe size={13} weight="fill" /> : <LockKey size={13} weight="fill" />}
                {isCommon ? "Comune" : "Privato"}
              </span>
              <span className="font-semibold text-sm">{job.name}</span>
              {job.category && <CategoryPill value={job.category} data-testid="job-category-pill" />}
              {job.subject && <SubjectPill value={job.subject} data-testid="job-subject-pill" />}
              <button onClick={() => setEditForm({ name: job.name, category: job.category || "", subject: job.subject || "" })} data-testid="rename-job-btn"
                className="p-1 text-muted-foreground hover:text-primary transition-colors" title="Modifica lavoro (nome, categoria, soggetto)"><PencilSimple size={15} /></button>
              <button onClick={doExportJob} data-testid="export-job-btn"
                className="p-1 text-muted-foreground hover:text-primary transition-colors" title="Esporta lavoro (.rowtool)"><DownloadSimple size={15} /></button>
              <button onClick={() => setDeleteOpen(true)} data-testid="delete-job-btn"
                className="p-1 text-muted-foreground hover:text-destructive transition-colors"><TrashSimple size={15} /></button>
            </div>
          )}

          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" onClick={() => setGuideOpen(true)} data-testid="guide-btn"
              className="gap-2 bg-gradient-to-r from-[hsl(var(--accent))] to-[hsl(var(--primary))] text-white hover:opacity-90 border-0">
              <Question size={16} weight="fill" /> Guida
            </Button>
            {job && <ModelManager job={job} setJob={setJob} levels={levels} setLevels={setLevels} userId={userId} onRecalled={setRecallBanner} />}
            <DropdownMenu open={jobsMenuOpen} onOpenChange={setJobsMenuOpen}>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="jobs-menu-btn" className="gap-2">
                  <StackSimple size={16} /> Lavori <CaretDown size={12} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-popover border-border w-72">
                <DropdownMenuItem onClick={() => { setJobsMenuOpen(false); setMyJobsOpen(true); }} data-testid="my-jobs-btn"
                  className="font-semibold text-primary">
                  <User size={14} className="mr-2" /> I Miei Lavori
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[11px] text-muted-foreground font-normal uppercase tracking-wide">Ultimi lavori</DropdownMenuLabel>
                {recentJobs.length === 0 && <div className="px-2 py-1 text-xs text-muted-foreground">Nessun lavoro</div>}
                {recentJobs.map((j) => (
                  <DropdownMenuItem key={j.id} onClick={() => selectJob(j.id)} data-testid={`job-item-${j.id}`} className="flex items-center gap-2">
                    <JobDots category={j.category} subject={j.subject} />
                    <span className="font-medium text-sm truncate">{j.name}</span>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={createJob} data-testid="new-job-btn" className="text-primary">
                  <Plus size={14} className="mr-2" /> Nuovo lavoro
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { setJobsMenuOpen(false); setTimeout(() => fileInputRef.current?.click(), 120); }} data-testid="import-job-btn">
                  <UploadSimple size={14} className="mr-2" /> Importa lavoro (.rowtool)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <div className="relative">
              <Button variant="outline" size="sm" onClick={accessCommon} data-testid="access-common-btn" className="gap-2">
                <Globe size={16} className="text-[hsl(var(--gold))]" /> Area comune
              </Button>
              {commonReleaseCount > 0 && (
                <span data-testid="common-release-badge"
                  className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center animate-pulse ring-2 ring-white">
                  {commonReleaseCount}
                </span>
              )}
            </div>

            {job && !isCommon && (
              <Button size="sm" onClick={() => setPwdDialog({ mode: "publish" })} data-testid="publish-btn"
                className="gap-2 bg-[hsl(var(--gold)/0.9)] hover:bg-[hsl(var(--gold))] text-black">
                <Globe size={16} /> Rendi comune
              </Button>
            )}
            {job && isCommon && (
              <>
                <Button size="sm" variant="outline" onClick={doUnpublish} data-testid="unpublish-btn" className="gap-2">
                  <LockKey size={16} /> Rendi privato
                </Button>
                <Button size="sm" variant="destructive" onClick={closeCommon} data-testid="close-common-btn" className="gap-2">
                  <X size={16} /> Chiudi lavoro
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-8">
        {!job ? (
          <EmptyState onCreate={createJob} onCommon={accessCommon} />
        ) : (
          <div className="space-y-10">
            {recallBanner && (
              <div className="rounded-xl border border-[hsl(var(--accent)/0.4)] bg-[hsl(var(--accent)/0.07)] p-4 flex items-start gap-3" data-testid="recall-banner">
                <StackSimple size={20} className="text-[hsl(var(--accent))] mt-0.5" />
                <div className="flex-1 text-sm">
                  <div className="font-semibold mb-1">Modello "{recallBanner.name}" richiamato — sorgenti previste:</div>
                  <ul className="space-y-0.5 text-muted-foreground">
                    {recallBanner.sources.map((s) => (
                      <li key={s.slot}>
                        <strong style={{ color: "hsl(var(--foreground))" }}>Fonte {s.slot}: {s.name}</strong>
                        {" "}— {s.columns.length} colonne{s.columns.length ? ` (${s.columns.slice(0, 8).join(", ")}${s.columns.length > 8 ? "…" : ""})` : ""}
                      </li>
                    ))}
                  </ul>
                  <div className="text-xs text-muted-foreground mt-1">Importa le sorgenti corrispondenti (Excel / PDF / Incolla) mantenendo queste colonne, poi esegui l'abbinamento.</div>
                </div>
                <button onClick={() => setRecallBanner(null)} data-testid="recall-banner-close" className="text-muted-foreground hover:text-foreground"><X size={16} /></button>
              </div>
            )}
            <DataSources job={job} setJob={setJob} />
            <RuleStack key={job.id} job={job} setJob={setJob} onMatched={setData} levels={levels} setLevels={setLevels} />
            {data.columns.length > 0 && (
              <>
                <ResultsGrid job={job} files={job.files} data={data} setData={setData} onActiveChange={setActiveRowIds} />
                <VerificationPlayground job={job} data={data} setData={setData} activeRowIds={activeRowIds} />
              </>
            )}
          </div>
        )}
      </main>

      {/* Password dialog */}
      <Dialog open={!!pwdDialog} onOpenChange={(o) => { if (!o) { setPwdDialog(null); setPwd(""); } }}>
        <DialogContent className="bg-popover border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck size={22} className="text-[hsl(var(--gold))]" />
              {pwdDialog?.mode === "publish" ? "Pubblica in area comune" : "Accedi all'area comune"}
            </DialogTitle>
            <DialogDescription>
              {pwdDialog?.mode === "publish"
                ? "Scegli una password libera: sarà richiesta agli altri utenti per accedere al lavoro comune."
                : `Inserisci la password del lavoro comune${pwdDialog?.name ? ` "${pwdDialog.name}"` : ""}.`}
            </DialogDescription>
          </DialogHeader>
          <Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submitPwd()}
            placeholder="Password" data-testid="common-password-input" className="bg-muted" autoFocus />
          {pwdDialog?.mode === "access" && !pwdDialog?.isOwner && pwdDialog?.requested && (
            <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-xs space-y-2" data-testid="force-eject-box">
              <p>Hai già chiesto al creatore di liberare l'area. Se l'area è rimasta bloccata puoi forzarne la chiusura: il lavoro comune verrà chiuso e svuotato per tutti.</p>
              <Button size="sm" variant="destructive" onClick={forceEject} data-testid="force-eject-btn">Forza Eject</Button>
            </div>
          )}
          {pwdDialog?.mode === "access" && !pwdDialog?.isOwner && !pwdDialog?.requested && (
            <button onClick={askRelease} data-testid="request-release-btn"
              className="text-xs text-left text-[hsl(var(--accent))] hover:underline flex items-center gap-1">
              <HandWaving size={14} /> Non hai la password o serve lo spazio? Chiedi al creatore di liberare l'area comune
            </button>
          )}
          <DialogFooter>
            <Button onClick={submitPwd} data-testid="common-password-submit-btn">
              {pwdDialog?.mode === "publish" ? "Pubblica" : "Entra"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create job dialog */}
      <JobFormDialog form={createForm} setForm={setCreateForm} onSubmit={doCreate}
        title="Nuovo lavoro" submitLabel="Crea lavoro" prefix="create" />

      {/* Edit job dialog */}
      <JobFormDialog form={editForm} setForm={setEditForm} onSubmit={doEdit}
        title="Modifica lavoro" submitLabel="Salva" prefix="edit" />

      {/* Delete confirm dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="bg-popover border-border max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><TrashSimple size={20} className="text-destructive" /> Elimina lavoro</DialogTitle>
            <DialogDescription>
              {isCommon
                ? "Questo è il lavoro comune: eliminandolo verrà chiuso e i dati svuotati per tutti. Continuare?"
                : "Sei sicuro di voler eliminare definitivamente questo lavoro?"}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>Annulla</Button>
            <Button variant="destructive" onClick={doDelete} data-testid="delete-confirm-btn">Elimina</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Guide dialog */}
      <GuideDialog open={guideOpen} onClose={() => setGuideOpen(false)} />

      {/* My jobs dialog */}
      <MyJobsDialog open={myJobsOpen} onClose={() => setMyJobsOpen(false)} jobs={jobs.private} currentJobId={job?.id}
        onSelect={(id) => { setMyJobsOpen(false); selectJob(id); }}
        onNew={() => { setMyJobsOpen(false); createJob(); }} />

      {/* Common release requests dialog (owner) */}
      <Dialog open={!!releaseDialog} onOpenChange={(o) => { if (!o) setReleaseDialog(null); }}>
        <DialogContent className="bg-popover border-border max-w-md" data-testid="release-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Bell size={20} weight="fill" className="text-red-600" /> Richiesta di liberare l'area comune</DialogTitle>
            <DialogDescription>
              {(releaseDialog?.release_requests || []).length} utente/i chiedono di liberare l'area comune
              {releaseDialog?.name ? ` «${releaseDialog.name}»` : ""}. Se liberi l'area, il lavoro comune verrà chiuso e i dati svuotati per tutti (esporta prima se ti servono).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={dismissRelease} data-testid="release-dismiss-btn">Mantieni · azzera avvisi</Button>
            <Button variant="destructive" onClick={freeCommon} data-testid="release-free-btn" className="gap-2">
              <X size={16} /> Libera l'area ora
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FilterChip({ active, onClick, label, color, outline, testid }) {
  const style = active
    ? (outline ? { borderColor: color, color, background: "transparent" } : { background: color || "hsl(var(--primary))", color: "#fff", borderColor: color || "hsl(var(--primary))" })
    : { borderColor: "hsl(var(--border))", color: "hsl(var(--muted-foreground))", background: "transparent" };
  return (
    <button onClick={onClick} data-testid={testid}
      className="text-[10px] px-2 py-0.5 rounded-full border font-medium transition-colors"
      style={{ ...style, borderWidth: 1.5 }}>
      {label}
    </button>
  );
}

const NONE = "__none__";
function JobFormDialog({ form, setForm, onSubmit, title, submitLabel, prefix }) {
  return (
    <Dialog open={!!form} onOpenChange={(o) => { if (!o) setForm(null); }}>
      <DialogContent className="bg-popover border-border max-w-md">
        <DialogHeader><DialogTitle>{title}</DialogTitle>
          <DialogDescription>Assegna nome, categoria e soggetto per ritrovare più facilmente i tuoi lavori.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Nome</label>
            <Input value={form?.name || ""} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              data-testid={`${prefix}-name-input`} className="bg-muted mt-1" autoFocus
              onKeyDown={(e) => e.key === "Enter" && onSubmit()} placeholder="Nome del lavoro" />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Categoria</label>
            <Select value={form?.category || NONE} onValueChange={(v) => setForm((f) => ({ ...f, category: v === NONE ? "" : v }))}>
              <SelectTrigger className="bg-muted mt-1" data-testid={`${prefix}-category-select`}><SelectValue placeholder="Categoria" /></SelectTrigger>
              <SelectContent className="bg-popover border-border">
                <SelectItem value={NONE} data-testid={`${prefix}-category-none`}>— Nessuna</SelectItem>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`${prefix}-category-${c}`}>
                    <span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: categoryColor(c) }} />{c}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Soggetto</label>
            <Select value={form?.subject || NONE} onValueChange={(v) => setForm((f) => ({ ...f, subject: v === NONE ? "" : v }))}>
              <SelectTrigger className="bg-muted mt-1" data-testid={`${prefix}-subject-select`}><SelectValue placeholder="Soggetto" /></SelectTrigger>
              <SelectContent className="bg-popover border-border">
                <SelectItem value={NONE} data-testid={`${prefix}-subject-none`}>— Nessuno</SelectItem>
                {SUBJECTS.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`${prefix}-subject-${s}`}>
                    <span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full border-2" style={{ borderColor: subjectColor(s) }} />{s}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {(form?.category || form?.subject) && (
            <div className="flex flex-wrap gap-1.5 pt-1" data-testid={`${prefix}-preview-pills`}>
              {form?.category && <CategoryPill value={form.category} />}
              {form?.subject && <SubjectPill value={form.subject} />}
            </div>
          )}
        </div>
        <DialogFooter><Button onClick={onSubmit} data-testid={`${prefix}-submit-btn`}>{submitLabel}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmptyState({ onCreate, onCommon }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-24 fade-up" data-testid="empty-state">
      <img src="/rowtool-logo.png" alt="RowTool" className="w-20 h-20 rounded-2xl mb-6" />
      <h1 className="text-4xl sm:text-5xl font-head font-extrabold mb-3">Incrocia i tuoi dati</h1>
      <p className="text-muted-foreground max-w-xl mb-8">
        Carica fino a 3 file (xls, xlsx, csv o incolla), configura livelli di abbinamento con rating
        gold/silver/bronze, verifica differenze e crea tool su misura con l'AI.
      </p>
      <div className="flex gap-3">
        <Button size="lg" onClick={onCreate} data-testid="start-new-job-btn" className="gap-2">
          <Plus size={18} /> Crea un lavoro
        </Button>
        <Button size="lg" variant="outline" onClick={onCommon} data-testid="start-common-btn" className="gap-2">
          <Globe size={18} className="text-[hsl(var(--gold))]" /> Area comune
        </Button>
      </div>
    </div>
  );
}
